import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Paperclip, Wrench, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, eur, km, dateFr, formatApiErrorDetail, API } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const TYPES = ["Vidange", "Filtre à air", "Filtre à huile", "Plaquettes de frein",
  "Disques de frein", "Pneus", "Courroie de distribution", "Batterie",
  "Contrôle technique", "Révision constructeur", "Climatisation", "Autre"];

const emptyOp = (vehicleId) => ({
  vehicle_id: vehicleId || "", type_entretien: "", date: new Date().toISOString().slice(0, 10),
  kilometrage: 0, garage: "", prix: 0, commentaire: "",
  prochain_km: "", prochaine_date: "",
});

export default function Maintenance() {
  const [params, setParams] = useSearchParams();
  const vehicleParam = params.get("vehicle") || "";
  const [vehicles, setVehicles] = useState([]);
  const [ops, setOps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyOp(vehicleParam));
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const fetchSuggestion = async (type_entretien, vehicle_id) => {
    if (!type_entretien || !vehicle_id) return setSuggestion(null);
    try {
      const { data } = await api.get("/periodicites", { params: { vehicle_id, type_entretien } });
      setSuggestion(data.suggestion);
    } catch {
      setSuggestion(null);
    }
  };

  const applySuggestion = () => {
    const next = { ...form };
    if (suggestion.intervalle_km) {
      next.prochain_km = (Number(form.kilometrage) || 0) + suggestion.intervalle_km;
    }
    if (suggestion.intervalle_mois) {
      const d = new Date(form.date || Date.now());
      d.setMonth(d.getMonth() + suggestion.intervalle_mois);
      next.prochaine_date = d.toISOString().slice(0, 10);
    }
    setForm(next);
    toast.success("Suggestion appliquée, vous pouvez l'ajuster");
  };

  const loadOps = useCallback(() => {
    const q = vehicleParam ? `?vehicle_id=${vehicleParam}` : "";
    return api.get(`/operations${q}`).then((r) => setOps(r.data));
  }, [vehicleParam]);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);
  useEffect(() => { loadOps(); }, [loadOps]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openForm = () => {
    setForm(emptyOp(vehicleParam || vehicles[0]?.id));
    setFile(null);
    setEditId(null);
    setOpen(true);
  };

  const startEdit = (o) => {
    setEditId(o.id);
    setFile(null);
    setForm({
      vehicle_id: o.vehicle_id, type_entretien: o.type_entretien,
      date: (o.date || "").slice(0, 10), kilometrage: o.kilometrage || 0,
      garage: o.garage || "", prix: o.prix || 0, commentaire: o.commentaire || "",
      prochain_km: "", prochaine_date: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Sélectionnez un véhicule");
    setSaving(true);
    try {
      let file_path = null, file_name = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_path = up.data.path;
        file_name = up.data.filename;
      }
      const payload = {
        ...form,
        kilometrage: Number(form.kilometrage) || 0,
        prix: Number(form.prix) || 0,
        prochain_km: form.prochain_km ? Number(form.prochain_km) : null,
        prochaine_date: form.prochaine_date || null,
        file_path, file_name,
      };
      if (editId) {
        await api.put(`/operations/${editId}`, payload);
        toast.success("Opération modifiée");
      } else {
        await api.post("/operations", payload);
        trackEvent("entretien_enregistre", {
          type_entretien: form.type_entretien,
          avec_justificatif: Boolean(file),
          avec_prochaine_echeance: Boolean(form.prochain_km || form.prochaine_date),
        });
        toast.success("Opération enregistrée");
      }
      setEditId(null);
      setOpen(false);
      loadOps();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/operations/${id}`);
      toast.success("Opération supprimée");
      loadOps();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const vehName = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? `${v.marque} ${v.modele}` : "Véhicule";
  };

  const total = ops.reduce((s, o) => s + (o.prix || 0), 0);

  return (
    <div data-testid="maintenance-page" className="space-y-8">
      <div className="flex items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Carnet d'entretien</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Entretien
          </h1>
        </div>
        <Button onClick={openForm} data-testid="open-operation-form-btn"
          className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Opération
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setParams({})} data-testid="filter-all-btn"
          className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            !vehicleParam ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}>
          Tous
        </button>
        {vehicles.map((v) => (
          <button key={v.id} onClick={() => setParams({ vehicle: v.id })} data-testid={`filter-vehicle-${v.id}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              vehicleParam === v.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            {v.marque} {v.modele}
          </button>
        ))}
      </div>

      {ops.length === 0 ? (
        <div data-testid="operations-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <Wrench className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="mt-4 font-display text-lg font-semibold text-slate-800">Aucune opération</p>
          <p className="mt-1 text-sm text-slate-500">Ajoutez votre première intervention.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-slate-800">{ops.length}</span> opération(s) — total{" "}
            <span className="font-semibold text-slate-800">{eur(total)}</span>
          </p>
          <ol data-testid="operations-list" className="relative border-l border-slate-200 ml-3 space-y-6">
            {ops.map((o, i) => (
              <li key={o.id} data-testid={`operation-item-${o.id}`}
                className="ml-6 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <span className="absolute -left-[7px] mt-5 h-3.5 w-3.5 rounded-full bg-blue-600 border-2 border-white" />
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-lg font-semibold text-slate-900">{o.type_entretien}</h3>
                      <p className="text-sm text-slate-500">
                        {dateFr(o.date)} · {km(o.kilometrage)} · {vehName(o.vehicle_id)}
                      </p>
                    </div>
                    <span className="font-display font-bold text-slate-900 whitespace-nowrap">{eur(o.prix)}</span>
                  </div>
                  {o.garage && <p className="mt-3 text-sm text-slate-600">Garage : {o.garage}</p>}
                  {o.commentaire && <p className="mt-1 text-sm text-slate-600">{o.commentaire}</p>}
                  <div className="mt-4 flex items-center justify-between">
                    {o.file_path ? (
                      <a href={`${API}/files/${o.file_path}?auth=${localStorage.getItem("mea_token")}`}
                        target="_blank" rel="noreferrer" data-testid={`operation-file-${o.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                        <Paperclip className="h-4 w-4" /> {o.file_name || "Justificatif"}
                      </a>
                    ) : <span />}
                    <button onClick={() => startEdit(o)} data-testid={`operation-edit-${o.id}`}
                      className="text-slate-400 hover:text-blue-600 transition-colors mr-3">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(o.id)} data-testid={`operation-delete-${o.id}`}
                      className="text-slate-400 hover:text-red-600 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="operation-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editId ? "Modifier l'opération" : "Nouvelle opération"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="operation-form">
            <div className="space-y-2">
              <Label>Véhicule</Label>
              <select required value={form.vehicle_id}
                onChange={(ev) => {
                  setForm({ ...form, vehicle_id: ev.target.value });
                  fetchSuggestion(form.type_entretien, ev.target.value);
                }}
                data-testid="operation-vehicle-select"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                <option value="">Choisir…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.marque} {v.modele}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Type d'entretien</Label>
              <select required value={form.type_entretien}
                onChange={(ev) => {
                  setForm({ ...form, type_entretien: ev.target.value });
                  fetchSuggestion(ev.target.value, form.vehicle_id);
                }}
                data-testid="operation-type-select"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                <option value="">Choisir…</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input required type="date" value={form.date} onChange={set("date")} data-testid="operation-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Kilométrage</Label>
                <Input type="number" value={form.kilometrage} onChange={set("kilometrage")} data-testid="operation-km-input" />
              </div>
              <div className="space-y-2">
                <Label>Garage / professionnel</Label>
                <Input value={form.garage} onChange={set("garage")} data-testid="operation-garage-input" placeholder="Garage Dupont" />
              </div>
              <div className="space-y-2">
                <Label>Prix (€)</Label>
                <Input type="number" step="0.01" value={form.prix} onChange={set("prix")} data-testid="operation-prix-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Textarea value={form.commentaire} onChange={set("commentaire")} data-testid="operation-comment-input" rows={3} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Prochaine échéance (optionnel)</p>
                <p className="text-xs text-slate-500">
                  Kilométrage, date, ou les deux. Le premier seuil atteint détermine l'urgence,
                  et le suivi « État de mon entretien » est mis à jour automatiquement.
                </p>
              </div>
              {suggestion && (
                <div data-testid="operation-suggestion"
                  className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-slate-700">
                    Périodicité couramment observée :{" "}
                    <span className="font-semibold">
                      {[suggestion.intervalle_km && `tous les ${suggestion.intervalle_km.toLocaleString("fr-FR")} km`,
                        suggestion.intervalle_mois && `tous les ${suggestion.intervalle_mois} mois`]
                        .filter(Boolean).join(" ou ")}
                    </span>
                    <span className="block text-xs text-slate-500">
                      Suggestion indicative, libre à vous de l'ajuster ou de l'ignorer.
                    </span>
                  </p>
                  <Button type="button" onClick={applySuggestion} data-testid="operation-apply-suggestion-btn"
                    className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
                    Appliquer
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prochain entretien à (km)</Label>
                  <Input type="number" value={form.prochain_km} onChange={set("prochain_km")}
                    data-testid="operation-prochain-km-input" placeholder="115000" />
                </div>
                <div className="space-y-2">
                  <Label>Prochain entretien le</Label>
                  <Input type="date" value={form.prochaine_date} onChange={set("prochaine_date")}
                    data-testid="operation-prochaine-date-input" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Facture ou photo (jpg, png, pdf — 10 Mo max)</Label>
              <Input type="file" accept="image/*,application/pdf" data-testid="operation-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <Button type="submit" disabled={saving} data-testid="operation-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement…" : "Enregistrer l'opération"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
